import type { LootPile } from "./LootPile.js";
import { takeAll } from "./LootPile.js";
import {
  addItem,
  remainingCapacity,
  type CharacterInventory,
} from "./CharacterInventory.js";

/**
 * I04 — "Loot all" button: dump an entire pile into the party member
 * with the most remaining carry capacity. Overflow stays on the pile
 * so the caller (UI) can show *"N items wouldn't fit."*
 *
 * "Strongest available" is **highest remaining capacity** per the spec.
 * Ties break on `order` to keep behaviour deterministic under test.
 *
 * Pure — callers pass snapshots in and get snapshots out. The runtime
 * reads the new pile back into `LootPileStore.update` and the new
 * inventory into its `CharacterInventory` slot.
 */

export interface PartyInventoryMember {
  readonly characterId: string;
  readonly strengthScore: number;
  readonly inventory: CharacterInventory;
  /**
   * Stable ordering index used only for tie-breaking when two members
   * have the same remaining capacity. Typically `formationSlot` order.
   */
  readonly order: number;
}

export interface LootAllResult {
  readonly pile: LootPile;
  /** The recipient's new inventory (unchanged if no one had capacity). */
  readonly inventory: CharacterInventory;
  /** characterId of the recipient; null when nothing could be taken. */
  readonly recipient: string | null;
  readonly movedQuantity: number;
  readonly refusedQuantity: number;
}

export function lootAllToStrongestCapacity(
  pile: LootPile,
  members: readonly PartyInventoryMember[],
): LootAllResult {
  if (members.length === 0 || pile.stacks.length === 0) {
    return {
      pile,
      inventory: members[0]?.inventory ?? { stacks: [], equipment: {} as never },
      recipient: null,
      movedQuantity: 0,
      refusedQuantity: 0,
    };
  }

  // Pick the member with the most remaining capacity (ties → lower order).
  const ranked = [...members]
    .map((m) => ({
      member: m,
      capacity: remainingCapacity(m.inventory, m.strengthScore),
    }))
    .sort((a, b) => b.capacity - a.capacity || a.member.order - b.member.order);

  const recipient = ranked[0]!.member;
  const { pile: emptied, stacks } = takeAll(pile);

  let inv = recipient.inventory;
  let moved = 0;
  let refused = 0;
  const leftovers: typeof stacks[number][] = [];

  for (const s of stacks) {
    const r = addItem(inv, s.item, s.quantity, recipient.strengthScore);
    inv = r.inventory;
    moved += r.added;
    if (r.refused > 0) {
      refused += r.refused;
      leftovers.push({ item: s.item, quantity: r.refused });
    }
  }

  const nextPile: LootPile = { ...emptied, stacks: leftovers };

  return {
    pile: nextPile,
    inventory: inv,
    recipient: recipient.characterId,
    movedQuantity: moved,
    refusedQuantity: refused,
  };
}
